﻿import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import Chart from 'react-apexcharts';

import Content_Container from '@Elements/Content_Container';

import { Customer_Week_Chart_Props } from '@Util/Types';

export default function Customer_Week_Chart(props: Customer_Week_Chart_Props): JSX.Element {
    const [t] = useTranslation();

    const legendItems = Array.from(new Set(props.weeks_time_log.map(log => log.customerName)));

    const chartData = useMemo(() => {
        if (!props.weeks_time_log || props.weeks_time_log.length === 0) {
            return [];
        }
    
        const daysData = new Array(7).fill(0).map(() => []);
        const customers = new Set();
    
        props.weeks_time_log.forEach(log => {
            const startDay = (dayjs(log.startDate).day() + 6) % 7;
            const endDay = (dayjs(log.endDate).day() + 6) % 7;
    
            for (let day = startDay; day <= endDay; day++) {
                const startMinute = day === startDay ? dayjs(log.startDate).hour() * 60 + dayjs(log.startDate).minute() : 0;
                const endMinute = day === endDay ? dayjs(log.endDate).hour() * 60 + dayjs(log.endDate).minute() : 24 * 60;
    
                daysData[day].push({
                    customerName: log.customerName,
                    duration: endMinute - startMinute,
                    color: log.color ? `rgb(${log.color})` : 'rgb(0, 0, 0)',
                });
    
                customers.add(log.customerName);
            }
        });
    
        // Calculate the total duration for each day
        const totalDurations = daysData.map(day => day.reduce((total, log) => total + log.duration, 0));
    
        // Calculate the percentage of the total duration for each log entry
        const series = Array.from(customers).map((customer: string) => {
            const customerLogs = new Array(7).fill(0).map(() => []);
    
            daysData.forEach((day, i) => {
                day.filter(log => log.customerName === customer).forEach(log => {
                    customerLogs[i].push({
                        percentage: log.duration / totalDurations[i] * 100,
                        color: log.color,
                    });
                });
            });
    
            const color = customerLogs.find(day => day.length > 0)[0].color;
    
            return {
                name: customer,
                data: customerLogs.map(day => day.length ? day.reduce((sum, log) => sum + log.percentage, 0) : null),
                color,
            };
        });
    
        return series;
    }, [props.weeks_time_log]);

    if (!chartData.length) {
        return null;
    }

    return (
        <section className="Module">
            <header className="Module_Header">
                <h2 className="Headline2">{t('Text.ThisWeek')}</h2>
            </header>
            <Content_Container>
                {chartData.length ? (
                    <Chart
                        options={{
                            chart: {
                                type: 'bar',
                                stacked: true,
                                toolbar: {
                                    show: true
                                },
                                zoom: {
                                    enabled: true
                                }
                            },
                            dataLabels: {
                                enabled: false
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                },
                            },
                            tooltip: {
                                custom: function ({ seriesIndex, dataPointIndex, w }) {
                                    const series = w.config.series[seriesIndex];
                                    const percentage = parseFloat(series.data[dataPointIndex].toFixed(2));
                                    return (
                                        '<div style="padding: 5px 10px; background-color: #282c32; color: #ffffff;">' +
                                        '<span>' + series.name + ': ' + percentage + '%</span>' +
                                        '</div>'
                                    );
                                }
                            },
                            xaxis: {
                                categories: [t('Monday'), t('Tuesday'), t('Wednesday'), t('Thursday'), t('Friday'), t('Saturday'), t('Sunday')],
                                labels: {
                                    style: {
                                        colors: '#929292'
                                    }
                                }
                            },
                            yaxis: {
                                min: 0,
                                max: 100,
                                labels: {
                                    formatter: (value) => `${value}%`,
                                    style: {
                                        colors: '#929292'
                                    }
                                },
                            },
                            legend: {
                                customLegendItems: legendItems,
                                formatter: (seriesName) => {
                                    return seriesName.split(' ')[0]; // Return the customer name without the index
                                },
                                position: 'bottom',
                                horizontalAlign: 'right',
                                labels: {
                                    colors: '#929292'
                                }
                            },
                            fill: {
                                opacity: 1,
                                colors: chartData.map(s => s.color),
                            },
                        }}
                        series={chartData}
                        type="bar"
                        height="350"
                    />
                ) : (
                    <Skeleton
                        width={'100%'}
                        height={350}
                        baseColor="#1c1e21"
                        highlightColor="#282c32"
                    />
                )}
            </Content_Container>
        </section>
    )
}